import { defineComponent as _defineComponent } from 'vue'
import type { BookV1, Publisher } from '@ilteducation/content-registry-types';
import { BaseState } from '@ilteducation/content-registry-types/enums/content-enums';
import { computed, ref } from 'vue';
import type { Language } from '@ilteducation/content-ref-data-types';
import ChipField from '@/components/base/ChipField.vue';
import eventBus from '@/foundations/services/eventBus';
import { defaultSubscriptions, Subscription } from '@/foundations/helpers/defaultSubscriptions';
import { defaultRealms, Realm } from '@/foundations/helpers/defaultRealms';
import { downloadExternalContent, getAllRefData, getAvailablePublishers } from '@/data-provider/refDataProvider';
import useMediaFormFeature from '@/comp/use-media-form-feature';

type Props = {
  title: string;
  resource: unknown;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'List',
  props: {
    title: null,
    resource: null
  },
  setup(__props: any) {



const availableServices = [
  {
    value: 'polylino',
    text: 'Polylino',
  },
  {
    value: 'audio-aid',
    text: 'Inlästa Läromedel',
  },
  {
    value: 'manabua',
    text: 'Begreppa',
  },
];

const availableSubscriptions = defaultSubscriptions;
const availablePublishers = ref<Publisher[]>([]);
const availableLanguages = ref<Language[]>([]);

getAvailablePublishers().then((publishers: Publisher[]) => {
  availablePublishers.value = publishers.sort((a: Publisher, b: Publisher) => a.name.localeCompare(b.name));
});
getAllRefData('languages').then((languages: Language[]) => {
  availableLanguages.value = languages.sort((a: Language, b: Language) => a.language.localeCompare(b.language));
});

const fields = [
  { source: 'cover', link: 'edit', },
  { source: 'isbn', link: 'edit', label: 'ISBN' },
  { source: 'title', link: 'edit', sortable: true },
  { source: 'originalTitle', sortable: true },
  { source: 'authors', sortable: true },
  {
    source: 'languages.mainLanguage',
    label: 'Main language',
    sortable: true,
    type: 'chip',
  },
  {
    source: 'realm',
    label: 'Market',
    sortable: true,
    type: 'chip',
    attributes: {
      color: 'orange',
    },
  },
  { source: 'ages', sortable: true },
  {
    source: 'accessRules.subscriptionType',
    label: 'Subscriptions',
    sortable: true,
  },
  { source: 'publisher.name', label: 'Publisher', sortable: true },
  { source: 'royaltyBeneficiaries.name', label: 'Royalty Beneficiaries', sortable: true },
  { source: 'state', sortable: true },
];

const filters = computed(() => [
  {
    source: 'state',
    type: 'select',
    attributes: {
      multiple: true,
    },
  },
  {
    source: 'originalTitle',
    type: 'text',
    multiple: true,
  },
  {
    source: 'title',
    type: 'text',
    multiple: true,
  },
  {
    source: 'authors',
    type: 'text',
    multiple: true,
  },
  {
    label: 'Language',
    source: 'languages.mainLanguage',
    type: 'autocomplete',
    attributes: {
      multiple: true,
      choices: availableLanguages.value,
      itemText: (item: Language) => item.language,
      itemValue: (item: Language) => item.languageCode,
      minChars: 1000,
    },
  },
  {
    source: 'publisher.name',
    label: 'Publisher',
    type: 'autocomplete',
    attributes: {
      multiple: true,
      choices: availablePublishers.value,
      itemText: (item: Publisher) => item.name,
      minChars: 1000,
    },
  },
  {
    source: 'royaltyBeneficiaries',
    label: 'Royalty Beneficiary',
    type: 'autocomplete',
    attributes: {
      multiple: true,
      choices: availablePublishers.value,
      itemText: (item: Publisher) => item.name,
      minChars: 1000,
    },
  },
  {
    source: 'realm',
    label: 'Market',
    type: 'autocomplete',
    attributes: {
      multiple: true,
      choices: defaultRealms,
      itemText: (item: Realm) => item.realm,
      itemValue: (item: Realm) => item.code,
      minChars: 1000,
    },
  },
  {
    source: 'accessRules.subscriptionType',
    label: 'Subscription',
    type: 'autocomplete',
    attributes: {
      multiple: true,
      choices: defaultSubscriptions,
      itemText: (item: Subscription) => item.subscription,
      minChars: 1000,
    },
  },
  {
    source: 'accessRules.services',
    label: 'Product',
    type: 'autocomplete',
    attributes: {
      multiple: true,
      choices: availableServices,
    },
  },
  {
    source: 'availabilityRealms',
    label: 'Available Markets',
    type: 'autocomplete',
    attributes: {
      multiple: true,
      choices: defaultRealms,
      itemText: (item: Realm) => item.realm,
      itemValue: (item: Realm) => item.code,
      minChars: 1000,
    },
  },
  {
    source: 'createdAt',
    label: 'Created',
    type: 'date',
  },
  {
    source: 'lastUpdatedAt',
    label: 'Last Updated',
    type: 'date',
  },
]);

const clipboardSuccessHandler = () => {
  eventBus.$emit('snackbar.open', 'Title has been copied successfully!');
};

const getCoverUrl = (id: string) => {
  const cfUrl = process.env.VUE_APP_COVERS_DOMAIN;
  return `https://${cfUrl}/bookCovers/${id}/cover.png?height=100`;
};

const getStateColor = (state: string) => {
  switch (state) {
    case 'published':
      return 'green';
    case 'disabled':
      return 'red';
    default:
      return 'yellow';
  }
};

const downloadCover = async (books: BookV1[]) => {
  const res = await Promise.allSettled(
    books.map((book: BookV1) => downloadExternalContent(book.isbn, 'cover', book.id, false)),
  );
  eventBus.$emit(
    'snackbar.open',
    `Started downloading ${res.filter((r: any) => r.value).length} of ${res.length} book covers`,
  );
};

const downloadEpub = async (books: BookV1[]) => {
  const res = await Promise.allSettled(
    books.map((book: BookV1) => downloadExternalContent(book.isbn, 'epub', book.id, false)),
  );
  eventBus.$emit(
    'snackbar.open',
    `Started downloading ${res.filter((r: any) => r.value).length} of ${res.length} ePub's`,
  );
};

const enableBooks = async (books: BookV1[]) => {
  const res = await Promise.allSettled(
    books
      .filter((book) => book.state === BaseState.Disabled)
      .map((book: BookV1) => {
        const { toggleMediaState } = useMediaFormFeature(book);
        return toggleMediaState();
      }),
  );
  eventBus.$emit('snackbar.open', `Enabled ${res.length} books`);
};
const disableBooks = async (books: BookV1[]) => {
  const res = await Promise.allSettled(
    books
      .filter((book) => book.state === BaseState.Published)
      .map((book: BookV1) => {
        const { toggleMediaState } = useMediaFormFeature(book);
        return toggleMediaState();
      }),
  );
  eventBus.$emit('snackbar.open', `Disabled ${res.length} books`);
};

return { __sfc: true,availableServices, availableSubscriptions, availablePublishers, availableLanguages, fields, filters, clipboardSuccessHandler, getCoverUrl, getStateColor, downloadCover, downloadEpub, enableBooks, disableBooks, ChipField }
}

})